/* Copyright (c) 2026 Hyegro Pty Ltd. All rights reserved. */

/**
 * Real Estate home in UI v2 (design-reference "Real Estate · Home").
 *
 * GET /re/dashboard, the same request the classic dashboard makes, plus its
 * v2 blocks: listings, enquiries by source (last 30 days) and this week's
 * dated items. A tile or panel appears only for a module the tenant holds;
 * the classic page's other figures (sales, leasing, mortgages, inventory)
 * follow under "More".
 */

import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Skeleton from "@mui/material/Skeleton";
import Typography from "@mui/material/Typography";
import { Plus } from "lucide-react";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import PageHeader from "components/v2/PageHeader";
import { EntityFormDialog } from "components/EntityFramework";
import { useAuth } from "auth/Auth";
import { usePermission } from "auth/usePermission";
import { displayName } from "shell-v2/greeting";
import { v2 as t } from "styles/tokens/v2";
import { formatDate, formatNumber, humanize, useReGet } from "../screens/api";
import { getReEntityConfig } from "../reEntityConfig";
import { localiseConfig } from "../reEntityVariant";
import useReVariant from "../useReVariant";
import { Panel, Pill, RE_ACCENT, StatTile, shortMoney } from "./parts";
import { greetingFor, sourceLabel } from "./logic";

const KIND = {
  AUCTION: { label: "Auction", tone: "warning" },
  VIEWING: { label: "Viewing", tone: "info" },
  INSTALLMENT: { label: "Instalment", tone: "purple" },
  RENEWAL: { label: "Renewal", tone: "danger" },
};

function SourceBars({ rows, pack }) {
  if (!rows.length) {
    return <Typography sx={{ fontSize: 14, color: t.text3 }}>No enquiries with a source in the last 30 days.</Typography>;
  }
  const max = Math.max(...rows.map((r) => r.count), 1);
  return (
    <Box component="ul" sx={{ listStyle: "none", m: 0, p: 0, display: "grid", gap: 1.75 }}>
      {rows.map((r) => (
        <Box component="li" key={r.source} sx={{ display: "grid", gridTemplateColumns: "minmax(110px, 30%) 1fr 48px", alignItems: "center", gap: 2 }}>
          <Typography sx={{ fontSize: 14, color: t.text }}>{sourceLabel(r.source, pack)}</Typography>
          <Box sx={{ height: 8, borderRadius: 4, bgcolor: t.hover, overflow: "hidden" }} aria-hidden>
            <Box sx={{ width: `${(r.count / max) * 100}%`, height: "100%", bgcolor: RE_ACCENT.ink, borderRadius: 4 }} />
          </Box>
          <Typography sx={{ fontSize: 14, fontWeight: 600, color: t.text, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
            {formatNumber(r.count)}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

function WeekList({ items, onOpen }) {
  if (!items.length) {
    return <Typography sx={{ fontSize: 14, color: t.text3 }}>Nothing dated in the next 7 days.</Typography>;
  }
  return (
    <Box component="ul" sx={{ listStyle: "none", m: 0, p: 0 }}>
      {items.map((item, i) => {
        const kind = KIND[item.kind] || { label: humanize(item.kind), tone: "neutral" };
        return (
          <Box
            component="li"
            key={`${item.kind}-${item.link}-${i}`}
            sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, py: 1.25, borderTop: i ? `1px solid ${t.border}` : "none" }}
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box
                component="button"
                type="button"
                onClick={() => onOpen(item.link)}
                sx={{ p: 0, border: 0, bgcolor: "transparent", cursor: "pointer", textAlign: "left", font: "inherit", fontSize: 14.5, fontWeight: 600, color: t.link, "&:hover": { textDecoration: "underline" } }}
              >
                {item.title}
              </Box>
              <Typography sx={{ fontSize: 13, color: t.text3 }}>{formatDate(item.at)}</Typography>
            </Box>
            <Pill tone={kind.tone}>{kind.label}</Pill>
          </Box>
        );
      })}
    </Box>
  );
}

export default function ReHomeV2() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const pack = useReVariant();
  const { canCreate: canCreateListing } = usePermission("re_listings");
  const { data: d, loading, error, reload } = useReGet("/re/dashboard");
  const [creating, setCreating] = useState(false);

  const held = new Set(d?.modules || []);
  const listingConfig = localiseConfig(getReEntityConfig("re-listings"), pack);
  const name = displayName(user);
  const canAddListing = held.has("RE_BROKERAGE") && canCreateListing;

  const tiles = [];
  if (d?.listings) {
    tiles.push(
      <StatTile key="active" label="Active listings" value={formatNumber(d.listings.active)}
        hint={d.listings.new_this_month ? `+${d.listings.new_this_month} this month` : null} hintTone="success"
        onClick={() => navigate("/real-estate/listings")} />,
      <StatTile key="offer" label="Under offer" value={formatNumber(d.listings.under_offer)}
        hint={Number(d.listings.under_offer_value) ? `${shortMoney(d.listings.under_offer_value)} combined` : null} />,
    );
  }
  if (d?.viewings) {
    tiles.push(
      <StatTile key="viewings" label={`${pack.labels?.viewing || "Viewing"}s, next 7 days`} value={formatNumber(d.viewings.upcoming_7d)}
        hint={`${formatNumber(d.viewings.completed_30d)} done in 30 days`} onClick={() => navigate("/real-estate/viewings")} />,
    );
  }
  if (d?.commissions) {
    const pipeline = Number(d.commissions.accrued || 0) + Number(d.commissions.eligible || 0);
    tiles.push(
      <StatTile key="commission" label="Commission pipeline" value={shortMoney(pipeline) || "—"} hint="Accrued and eligible"
        onClick={() => navigate("/real-estate/commissions")} />,
    );
  }

  const more = [];
  if (d?.sales) {
    more.push(
      <StatTile key="sales" label="Sales value, this month" value={shortMoney(d.sales.value_mtd) || "—"}
        hint={`${formatNumber(d.sales.bookings_mtd)} ${(pack.labels?.booking || "Booking").toLowerCase()}s`} onClick={() => navigate("/real-estate/bookings")} />,
      <StatTile key="overdue" label="Overdue instalments" value={shortMoney(d.sales.overdue_amount) || "—"}
        hint={`${formatNumber(d.sales.overdue_count)} instalments`} hintTone={d.sales.overdue_count ? "danger" : "neutral"} />,
    );
  }
  if (d?.leasing) {
    more.push(
      <StatTile key="leases" label="Active leases" value={formatNumber(d.leasing.active)}
        hint={`${formatNumber(d.leasing.expiring_90d)} ending in 90 days`} hintTone={d.leasing.expiring_90d ? "warning" : "neutral"}
        onClick={() => navigate("/real-estate/leases")} />,
    );
  }
  if (d?.mortgages) {
    const open = Object.entries(d.mortgages.by_stage || {})
      .filter(([stage]) => !["DISBURSED", "DECLINED", "WITHDRAWN"].includes(stage))
      .reduce((sum, [, n]) => sum + Number(n || 0), 0);
    more.push(
      <StatTile key="mortgages" label="Mortgage applications in progress" value={formatNumber(open)}
        onClick={() => navigate("/real-estate/mortgage-applications")} />,
    );
  }
  if (d?.inventory) {
    more.push(
      <StatTile key="inventory" label="Units available" value={formatNumber(d.inventory.AVAILABLE)}
        hint={`${formatNumber(d.inventory.SOLD)} sold`} onClick={() => navigate("/real-estate/inventory")} />,
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Box sx={{ pt: 3, pb: 6 }}>
        <PageHeader
          title={`${greetingFor()}${name ? `, ${name}` : ""}`}
          summary="Your agency at a glance"
          actions={canAddListing ? (
            <Button variant="contained" startIcon={<Plus size={16} />} onClick={() => setCreating(true)} sx={{ color: "#FFFFFF !important" }}>
              New listing
            </Button>
          ) : null}
        />

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} action={<Button onClick={reload}>Retry</Button>}>
            The Real Estate figures could not be loaded.
          </Alert>
        )}

        {loading && !d ? (
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", lg: "repeat(4, 1fr)" }, gap: 2 }}>
            {[0, 1, 2, 3].map((i) => <Skeleton key={i} variant="rounded" height={120} />)}
          </Box>
        ) : (
          <>
            {tiles.length > 0 && (
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", lg: `repeat(${Math.min(tiles.length, 4)}, 1fr)` }, gap: 2, mb: 2 }}>
                {tiles}
              </Box>
            )}

            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "3fr 2fr" }, gap: 2, mb: 2 }}>
              <Panel title="Enquiries by source" aside="Last 30 days">
                <SourceBars rows={d?.enquiries_by_source || []} pack={pack} />
              </Panel>
              <Panel title="This week" aside={`${(d?.this_week || []).length} items`}>
                <WeekList items={d?.this_week || []} onOpen={(link) => navigate(link)} />
              </Panel>
            </Box>

            {more.length > 0 && (
              <>
                <Typography component="h2" sx={{ fontSize: 13, fontWeight: 600, color: t.text3, textTransform: "uppercase", letterSpacing: "0.06em", mt: 3, mb: 1.5 }}>
                  More
                </Typography>
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", lg: "repeat(4, 1fr)" }, gap: 2 }}>
                  {more}
                </Box>
              </>
            )}
          </>
        )}
      </Box>

      {canAddListing && (
        <EntityFormDialog
          open={creating}
          onClose={() => setCreating(false)}
          title="New listing"
          apiPath="/re/listings"
          fields={listingConfig.createForm.fields}
          onCreated={(row) => {
            setCreating(false);
            if (row?.id) navigate(`/real-estate/listings/${row.id}`);
          }}
        />
      )}
    </DashboardLayout>
  );
}
